import React, { memo, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FaHeart, FaRegHeart, FaSort, FaSortAmountDown, FaSortAmountUp, FaTrash, FaClipboard, FaCheck, FaRecycle, FaChevronRight, FaGlobe } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import ImageCarousel from './ImageCarousel';
import ConfirmDialog from './ConfirmDialog';
import NumberSelectionDialog from './NumberSelectionDialog';
import RetroFreeDrawing from './RetroFreeDrawing';
import RetroLoadingDots from './RetroLoadingDots';
import './ImageGrid.css';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import ImageCard from './ImageCard';
import ImageCardSmall from './ImageCardSmall';
import ImageGridControls from './ImageGridControls';
import { getEmojisFromActiveSpices } from './SpiceRack';
import { getRandomActiveGame } from '../utils/gameSelector';

const CreateImageGrid = ({ 
  images = [], 
  isLoading,
  favorites = [],
  onToggleFavorite,
  onDelete,
  newImageIds,
  generationProgress = {},
  onImageClick,
  onFavorite,
  onImageLoaded,
  onRegenerate,
  resetRotationKey,
  isGroupedByPrompt,
  setIsGroupedByPrompt,
  sortOrder,
  setSortOrder,
  showFavoriteCount,
  isHighlightingLikes,
  setIsHighlightingLikes,
  isHighlightingPublic,
  setIsHighlightingPublic,
  onLoadMore,
  hasMore,
  loadingMore,
  onCopyToPrompt,
  showFavoritesOnly,
  setShowFavoritesOnly,
  isAuthenticated,
  favoritesCount,
  publicCount,
  showPublicOnly,
  setShowPublicOnly,
  isCompactGrid = false,
  setIsCompactGrid = () => {},
  onFullscreenClick = () => {}
}) => {
  // Convert favorites array to Set for efficient lookups
  const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

  // Track which images have finished loading to manage placeholder visibility
  const [loadedImages, setLoadedImages] = useState(new Set());
  // Track which prompt groups are collapsed in grouped view
  const [collapsedPrompts, setCollapsedPrompts] = useState(new Set());
  // Track if we should show likes in popular view
  const [isShowingPopularLikes, setIsShowingPopularLikes] = useState(false);
  // Track view transitions to prevent flickering "no images found" (but no spinner)
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Track which generating cards have been clicked to start games
  const [clickedGeneratingCards, setClickedGeneratingCards] = useState(new Set());
  // Cache selected games for each placeholder to prevent re-randomization on hover
  const [selectedGames, setSelectedGames] = useState(new Map());

  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const handleToggleFavorite = (imageId) => {
    onToggleFavorite(imageId);
    if (!favoritesSet.has(imageId)) {
      onFavorite?.();
    }
  };

  const handleImageLoad = (imageId) => {
    setLoadedImages(prev => {
      const next = new Set(prev).add(imageId);
      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {

      }
      onImageLoaded?.(imageId);
      return next;
    });
  };

  const handleGeneratingCardClick = (imageId) => {
    setClickedGeneratingCards(prev => new Set(prev).add(imageId));
  };

  // Cleanup cached games when generation completes
  useEffect(() => {
    Object.entries(generationProgress).forEach(([genId, progress]) => {
      if (progress?.status === 'complete') {
        const placeholderId = `placeholder-${genId}`;
        setSelectedGames(prev => {
          const next = new Map(prev);
          next.delete(placeholderId);
          return next;
        });
        setClickedGeneratingCards(prev => {
          const next = new Set(prev);
          next.delete(placeholderId);
          return next;
        });
      }
    });
  }, [generationProgress]);

  // Initialize games for new placeholders  
  useEffect(() => {
    if (!newImageIds?.size) return;
    
    const needsGames = Array.from(newImageIds).filter(id => !selectedGames.has(id));
    
    if (needsGames.length > 0) {
      setSelectedGames(prev => {
        const next = new Map(prev);
        needsGames.forEach(id => {
          next.set(id, getRandomActiveGame());
        });
        return next;
      });
    }
  }, [newImageIds?.size]); // Only re-run when the size changes

  // Group placeholders by their generation ID to show loading state
  // Format: { generationId: [placeholderId1, placeholderId2, ...] }
  const placeholdersByGeneration = useMemo(() => {
    const groups = {};
    // newImageIds is a Set, so we use forEach instead of array methods
    newImageIds?.forEach(id => {
      const [, generationId] = id.split('-');
      if (generationId) {
        if (!groups[generationId]) {
          groups[generationId] = [];
        }
        groups[generationId].push(id);
      }
    });
    return groups;
  }, [newImageIds]);

  // Find images being used as source for generation
  const sourceImageIds = useMemo(() => {
    const ids = new Set();
    Object.values(generationProgress).forEach(progress => {
      // Only add the source image ID if the generation is not complete
      if (progress.status !== 'complete') {
        // Check for direct image ID first
        if (progress.sourceImageId) {
          ids.add(progress.sourceImageId);
        }
        // Then check for source image from URL
        else if (progress.sourceImageUrl) {
          // Extract ID from URL if possible
          const urlMatch = progress.sourceImageUrl.match(/\/([^\/]+)\.[^\.]+$/);
          if (urlMatch) {
            ids.add(urlMatch[1]);
          }
        }
        // Finally check generation ID
        else if (progress.sourceGenerationId) {
          ids.add(progress.sourceGenerationId);
        }
      }
    });
    return ids;
  }, [generationProgress]);

  // Find source image for regeneration placeholders
  const getSourceImageForGeneration = useCallback((generationId) => {
    const progress = generationProgress[generationId];
    if (progress?.sourceImageUrl) {
      return progress.sourceImageUrl;
    }
    if (progress?.sourceImageId) {
      const sourceImage = images.find(img => img.id === progress.sourceImageId);
      return sourceImage?.s3Url || sourceImage?.url;
    }
    if (progress?.sourceGenerationId) {
      const sourceImage = images.find(img => img.id === progress.sourceGenerationId);
      return sourceImage?.s3Url || sourceImage?.url;
    }
    return null;
  }, [images, generationProgress]);

  // Reset sort order to 'newest' when new images are added
  useEffect(() => {
    if (newImageIds?.length > 0) {
      // Remove this as it's now handled by parent
      // setSortOrder('newest');
    }
  }, [newImageIds]);

  // Track view transitions to prevent flickering "no images found"
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 200); // Short timeout just to prevent flash
    
    return () => clearTimeout(timer);
  }, [showFavoritesOnly, showPublicOnly]);

  // Reset transitioning state when loading starts OR when we have images again
  useEffect(() => {
    if (isLoading || images.length > 0) {
      setIsTransitioning(false);
    }
  }, [isLoading, images.length]);

  // Detect if we're in an inconsistent state (filters active but no images = likely loading)
  const isInconsistentState = useMemo(() => {
    // If we have filters active but no images, we're probably loading
    return images.length === 0 && (showFavoritesOnly || showPublicOnly);
  }, [images.length, showFavoritesOnly, showPublicOnly]);

  // Group and sort images
  const processedImages = useMemo(() => {
    let processed = images;

    if (isGroupedByPrompt) {
      // Group by prompt and sort groups by most recent image
      const groups = processed.reduce((acc, img) => {
        const prompt = img.prompt || '';
        if (!acc[prompt]) {
          acc[prompt] = [];
        }
        acc[prompt].push(img);
        return acc;
      }, {});

      // Sort images within each group
      Object.values(groups).forEach(group => {
        group.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });

      // Sort groups by most recent image in each group and return with prompts
      return Object.entries(groups)
        .sort(([, groupA], [, groupB]) => {
          const latestA = groupA[0].createdAt;
          const latestB = groupB[0].createdAt;
          return new Date(latestB) - new Date(latestA);
        })
        .map(([prompt, images]) => ({ prompt, images }));
    } else {
      // Regular sorting by date or popularity
      return [{
        prompt: null,
        images: processed.sort((a, b) => {
          if (sortOrder === 'newest') {
            return new Date(b.createdAt) - new Date(a.createdAt);
          } else if (sortOrder === 'oldest') {
            return new Date(a.createdAt) - new Date(b.createdAt);
          } else if (sortOrder === 'popular') {
            // Sort by favorite count, then by date if counts are equal
            const countDiff = (b.favoriteCount || 0) - (a.favoriteCount || 0);
            return countDiff !== 0 ? countDiff : new Date(b.createdAt) - new Date(a.createdAt);
          }
          return new Date(b.createdAt) - new Date(a.createdAt);
        })
      }];
    }
  }, [images, sortOrder, isGroupedByPrompt]);

  // Add this new function to find differences between prompts
  const findPromptDifferences = useMemo(() => {
    if (!isGroupedByPrompt) return {};
    
    // Get all prompts
    const allPrompts = processedImages.map(group => group.prompt).filter(Boolean);
    
    // Find the common parts and differences for each prompt
    return allPrompts.reduce((acc, prompt) => {
      // Split prompt into words and normalize
      const words = prompt.split(/\s+/).map(word => ({
        word,
        normalized: word.toLowerCase().replace(/[.,]/g, '')
      }));
      
      // Find similar prompts (ones that are >80% similar)
      const similarPrompts = allPrompts.filter(p => {
        if (p === prompt) return false;
        const similarity = words.filter(({ normalized }) => 
          p.toLowerCase().includes(normalized)
        ).length / words.length;
        return similarity > 0.8;
      });
      
      // If we have similar prompts, identify unique words
      if (similarPrompts.length > 0) {
        const uniqueWords = words.map(({ word, normalized }) => {
          const isUnique = similarPrompts.every(otherPrompt => 
            !otherPrompt.toLowerCase().includes(normalized)
          );
          return { word, isUnique };
        });
        
        acc[prompt] = uniqueWords;
      } else {
        // If no similar prompts, treat all words as non-unique
        acc[prompt] = words.map(({ word }) => ({ word, isUnique: false }));
      }
      
      return acc;
    }, {});
  }, [processedImages, isGroupedByPrompt]);

  const togglePromptCollapse = (prompt) => {
    setCollapsedPrompts(prev => {
      const next = new Set(prev);
      if (next.has(prompt)) {
        next.delete(prompt);
      } else {
        next.add(prompt);
      }
      return next;
    });
  };

  // Move renderImages inside the component
  const renderImages = (groupImages, {
    generationProgress,
    placeholdersByGeneration,
    getSourceImageForGeneration,
    loadedImages,
    favorites,
    handleToggleFavorite,
    onDelete,
    onImageClick,
    onFavorite,
    handleImageLoad,
    onRegenerate,
    resetRotationKey,
    sourceImageIds,
    isGroupedByPrompt,
    showFavoriteCount,
    newImageIds,
    isHighlightingLikes,
    isHighlightingPublic,
    onCopyToPrompt,
    isCompactGrid
  }) => {
    return groupImages.map(image => {
      // If this is a placeholder image
      if (image.id.startsWith('placeholder-')) {
        const [, generationId] = image.id.split('-');
        const progress = generationProgress[generationId];
        const sourceImage = getSourceImageForGeneration(generationId);



        // Create a placeholder image object that matches the ImageCard interface
        const placeholderImage = {
          id: image.id,
          url: sourceImage,
          prompt: progress?.prompt || '',
          isPlaceholder: true,
          current: progress?.current || 0,
          total: progress?.total || 1,
          didInteract: false // Add this to prevent auto-rotation
        };

        // Check if this generating card has been clicked
        const hasBeenClicked = clickedGeneratingCards.has(image.id);

                  return (
            <motion.div
              key={image.id}
              layout="position"
              transition={{
                layout: { type: "spring", bounce: 0.2, duration: 0.6 }
              }}
            >
              {(() => {
                // Choose component based on compact mode for placeholders too
                const CardComponent = isCompactGrid ? ImageCardSmall : ImageCard;
                
                return (
                  <CardComponent
                    image={placeholderImage}
                    isFavorited={false}
                    isNew={true}
                    isGenerating={false}
                    layoutId={`placeholder-${image.id}`}
                    onClick={() => {
                      if (!hasBeenClicked) {
                        // Always assign a new random game on click for immediate start
                        const newGame = getRandomActiveGame();
                        setSelectedGames(prev => new Map(prev).set(image.id, newGame));
                        handleGeneratingCardClick(image.id);
                      }
                    }}
                    // Only pass hideHoverButtons to full ImageCard
                    {...(isCompactGrid ? {} : {
                      hideHoverButtons: false
                    })}
                    renderOverlay={(progress?.status !== 'complete') ? () => {
                  // If not clicked yet, show "Creating" animation
                  if (!hasBeenClicked) {
                    return (
                      <div className="absolute inset-0 bg-black rounded-xl flex items-center justify-center cursor-pointer">
                        <RetroLoadingDots 
                          text="Creating"
                          size="28px" 
                          className="text-white" 
                        />
                      </div>
                    );
                  }
                  
                  // If clicked, show the game
                  const selectedGame = selectedGames.get(image.id);
                  
                  // If no game is cached yet, fallback to first available game
                  if (!selectedGame) {
                    console.warn('[RETRO] 🎮 No cached game found, using fallback');
                    return null;
                  }
                  

                  
                  const GameComponent = selectedGame.component;
                  
                  return (
                    <div className="absolute inset-0 bg-black rounded-xl overflow-hidden">
                      <div 
                        className="w-full h-full relative"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <div 
                          className="w-full h-full"
                          style={{ 
                            transform: 'scale(0.8)',
                            transformOrigin: 'center',
                            maxWidth: '100%',
                            maxHeight: '100%'
                          }}
                        >
                          <GameComponent
                            isActive={hasBeenClicked}
                            generatedImage={sourceImage ? {
                              id: generationId,
                              url: sourceImage,
                              prompt: progress?.prompt || ''
                            } : null}
                            showStartButton={false}
                            componentId={`${selectedGame.id}-${generationId}`}
                            isGenerating={selectedGame.id === 'drawing' ? false : progress?.status !== 'complete'}
                            activeSpices={image.activeSpices || []}
                          />
                        </div>
                      </div>
                    </div>
                  );
                } : null}
                  />
                );
              })()}
            </motion.div>
          );
      }

      // For regular images, use the normal ImageCard
      const isNewImage = newImageIds?.has?.(image.id);
      const generationId = image.generationId;
      const isGenerating = generationId && generationProgress[generationId]?.status !== 'complete';
      
      // Skip rendering if this is a generating image that hasn't loaded yet
      if (isGenerating && isNewImage && !loadedImages.has(image.id)) {
        return null;
      }

      return (
        <motion.div
          key={image.id}
          layout="position"
          transition={{
            layout: { type: "spring", bounce: 0.2, duration: 0.6 }
          }}
        >
          {(() => {
            // Choose component based on compact mode
            const CardComponent = isCompactGrid ? ImageCardSmall : ImageCard;
            
            return (
              <CardComponent
                key={image.id}
                image={image}
                onImageLoaded={() => handleImageLoad(image.id)}
                isFavorited={favorites.has(image.id)}
                onToggleFavorite={() => handleToggleFavorite(image.id)}
                onClick={() => {
                  onImageClick?.(image);
                }}
                isGenerating={sourceImageIds.has(image.id)}
                isNew={newImageIds?.has(image.id)}
                // Only pass these props to the full ImageCard, not ImageCardSmall
                {...(isCompactGrid ? {} : {
                  forceShowLikes: false,
                  showFavoriteCount: showFavoriteCount,
                  onDelete: onDelete,
                  onFavorite: onFavorite,
                  onRegenerate: onRegenerate,
                  isHighlightingLikes: isHighlightingLikes,
                  isHighlightingPublic: isHighlightingPublic,
                  onCopyToPrompt: onCopyToPrompt,
                  hideHoverButtons: false
                })}
              />
            );
          })()}
        </motion.div>
      );
    });
  };

  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && !isLoading && !loadingMore && hasMore) {
      onLoadMore?.();
    }
  }, [onLoadMore, isLoading, loadingMore, hasMore]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver(handleObserver, options);
    
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  return (
    <div className="space-y-8">
      {/* Loading indicator - only show for initial load, not filter transitions */}
      {isLoading && images.length === 0 && !showFavoritesOnly && !showPublicOnly && (
        <div className="flex flex-col items-center justify-center py-20">
          <motion.div 
            className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      )}

      {/* Gallery Grid */}
      {images.length === 0 && !isLoading && !isTransitioning && !isInconsistentState && (
        <div className="flex flex-col items-center justify-center py-20 text-black/40">
          <p className="text-[24px] font-['VT323'] mb-2">No images found</p>
          <p className="text-[18px] font-['VT323']">
            {showFavoritesOnly ? "You haven't liked any images yet" :
             showPublicOnly ? "No public images available" :
             "Start by generating some images"}
          </p>
        </div>
      )}

      {(images.length > 0 || isLoading) && (
        <motion.div layout="position">
          {processedImages.map(({ prompt, images: groupImages }, index) => (
            <motion.div 
              key={prompt || `group-${index}`}
              layout="position"
              initial={false}
              transition={{
                layout: { type: "spring", bounce: 0.1, duration: 0.6 }
              }}
              onMouseEnter={() => sortOrder === 'popular' && setIsShowingPopularLikes(true)}
              onMouseLeave={() => setIsShowingPopularLikes(false)}
            >
              {/* Prompt title and container for grouped view */}
              {isGroupedByPrompt && prompt ? (
                <div className="relative rounded-2xl overflow-hidden mb-6" style={{ overflow: 'visible' }}>
                  {/* Prompt Header Card - More prominent */}
                  <motion.div 
                    layout="position"
                    className="cursor-pointer bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm border border-black/5 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow mb-4"
                    onClick={() => togglePromptCollapse(prompt)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Count badge */}
                        <div className="inline-flex items-center gap-2 mb-2">
                          <span className="text-[14px] font-['VT323'] text-black/40 bg-black/5 px-2 py-0.5 rounded-full">
                            {groupImages.length} image{groupImages.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {/* Prompt text */}
                        <h3 className="text-[20px] font-['VT323'] leading-relaxed text-black/90 mb-2">
                          {groupImages[0]?.basePrompt || ''}
                        </h3>
                        
                        {/* Style emojis */}
                        {(() => {
                          const styleEmojis = getEmojisFromActiveSpices(groupImages[0]?.spiceIds || []);
                          return styleEmojis.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {[...new Set(styleEmojis)].map((emoji, index) => (
                                <span key={`${emoji}-${index}`} className="text-xl opacity-70">
                                  {emoji}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Collapse indicator */}
                      <motion.div
                        animate={{ rotate: collapsedPrompts.has(prompt) ? -90 : 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                        className="flex-shrink-0 text-black/30 hover:text-black/50 transition-colors"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Images Grid for grouped view */}
                  <motion.div 
                    layout="position"
                    className={`grid auto-rows-fr ${
                      isCompactGrid 
                        ? 'gap-2 grid-cols-4 sm:grid-cols-6 lg:grid-cols-8' 
                        : 'gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                    }`}
                    style={{
                      height: collapsedPrompts.has(prompt) ? 0 : 'auto',
                      overflow: collapsedPrompts.has(prompt) ? 'hidden' : 'visible',
                      padding: isCompactGrid ? '8px' : '4px'
                    }}
                    animate={{ 
                      opacity: collapsedPrompts.has(prompt) ? 0 : 1,
                      height: collapsedPrompts.has(prompt) ? 0 : 'auto'
                    }}
                    transition={{
                      layout: { type: "spring", bounce: 0.1, duration: 0.4 },
                      opacity: { duration: 0.3 },
                      height: { duration: 0.3 }
                    }}
                  >
                    {renderImages(groupImages, {
                      generationProgress,
                      placeholdersByGeneration,
                      getSourceImageForGeneration,
                      loadedImages,
                      favorites: favoritesSet,
                      handleToggleFavorite,
                      onDelete,
                      onImageClick,
                      onFavorite,
                      handleImageLoad,
                      onRegenerate,
                      resetRotationKey,
                      sourceImageIds,
                      isGroupedByPrompt,
                      showFavoriteCount,
                      newImageIds,
                      isHighlightingLikes: isHighlightingLikes || (sortOrder === 'popular' && isShowingPopularLikes),
                      isHighlightingPublic,
                      onCopyToPrompt,
                      isCompactGrid
                    })}
                  </motion.div>
                </div>
              ) : (
                <motion.div 
                  layout="position"
                  className={`grid auto-rows-fr ${
                    isCompactGrid 
                      ? 'gap-2 grid-cols-4 sm:grid-cols-6 lg:grid-cols-8' 
                      : 'gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                  }`}
                  style={{ 
                    overflow: 'visible',
                    padding: isCompactGrid ? '8px' : '16px'
                  }}
                  transition={{
                    layout: { type: "spring", bounce: 0.1, duration: 0.6 }
                  }}
                >
                  {renderImages(groupImages, {
                    generationProgress,
                    placeholdersByGeneration,
                    getSourceImageForGeneration,
                    loadedImages,
                    favorites: favoritesSet,
                    handleToggleFavorite,
                    onDelete,
                    onImageClick,
                    onFavorite,
                    handleImageLoad,
                    onRegenerate,
                    resetRotationKey,
                    sourceImageIds,
                    isGroupedByPrompt,
                    showFavoriteCount,
                    newImageIds,
                    isHighlightingLikes: isHighlightingLikes || (sortOrder === 'popular' && isShowingPopularLikes),
                    isHighlightingPublic,
                    onCopyToPrompt,
                    isCompactGrid
                  })}
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Load More Button */}
      {hasMore && images.length > 0 && images.length % 20 === 0 && (
        <div className="flex justify-center pt-4">
          <motion.button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-6 py-3 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors relative overflow-hidden disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loadingMore ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-black/10 border-t-black/40 animate-spin" />
                Loading...
              </div>
            ) : (
              'Load More'
            )}
          </motion.button>
        </div>
      )}

      {/* Loading trigger element */}
      {hasMore && (
        <div 
          ref={loadMoreRef}
          className="h-20 flex items-center justify-center"
        >
          {loadingMore && (
            <motion.div 
              className="w-8 h-8 border-2 border-black/20 border-t-black/40 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(CreateImageGrid); 